// eslint no-unused-vars
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-router-dom';
import imgLogo from '../../../components/navbar/logo.png';
import { Icon } from '../../../components/Icon';
import styles from './ResponsiveMode.mobile.module.css';
import { ResponsiveModeContext } from './ResponsiveModeContext';
import { Carousel } from './Carousel';
import JSXStyle from 'styled-jsx/style';
import { AppContext } from '../../../services/AppContext';
import useDebounce from '../../../hooks/use-debounce';
import { useEffect } from 'react';

export const ResponsiveMobileContext = React.createContext({});

export const ResponsiveModeMobile = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const { player } = useContext(ResponsiveModeContext);
	const [mode, setMode] = useState('issue');
	const [isTocOpen, setIsTocOpen] = useState(false);
	const [isPlayerOpen, setIsPlayerOpen] = useState(false);
	return (
		<div className={['App', styles['issue']].join(' ')}>
			<ResponsiveMobileContext.Provider
				value={{
					setMode,
					mode,
					isTocOpen,
					searchTerm,
					onSearchChange: setSearchTerm,
					isPlayerOpen: !!(isPlayerOpen && player),
					setIsPlayerOpen: setIsPlayerOpen,
					togglePlayer: () => setIsPlayerOpen(!isPlayerOpen),
					toggleToc: () => setIsTocOpen(!isTocOpen),
				}}
			>
				{mode === 'search' ? <SearchMode /> : <IssueMode />}
			</ResponsiveMobileContext.Provider>
		</div>
	);
};

const IssueMode = () => {
	const { getReadTime, config, isIframeMode, theme } = useContext(AppContext);
	const { article, articles, lang, onTocItemClick } = useContext(
		ResponsiveModeContext
	);
	const { isTocOpen, toggleToc } = useContext(ResponsiveMobileContext);
	const asideClassName = [styles['toc']];
	let shareButton = null;
	const { readTime } = config;
	const { color, background } = theme.navbar;
	if (navigator) {
		let onShareClick = () => {
			const el = document.createElement('textarea');
			el.value = window.location.href;
			document.body.appendChild(el);
			el.select();
			document.execCommand('copy');
			document.body.removeChild(el);
			alert('Copy url to the clipboard');
		};
		if (navigator.share) {
			onShareClick = () => {
				const share = {
					title: article?.name?.[lang] || '',
					text: article?.description || '',
					url: window.location.href,
				};
				navigator.share(share);
			};
		}
		shareButton = (
			<button onClick={onShareClick} title={'Share'} aria-label={'Share'}>
				<Icon name={'share'} color={color} />
			</button>
		);
	}

	() => {
		navigator.clipboard.writeText(window.location.href);
	};

	let className = styles['issue-view']
	if(isIframeMode) {
		className = styles['iframe-view']
	}

	return (
		<div className={className}>
			<nav className={styles['navbar']} style={{background}}>
				<div>
					<Link to={'/'}>
						<img src={imgLogo} alt="logo" />
					</Link>
				</div>
				<div>{shareButton}</div>
			</nav>
			<main
				className={
					isTocOpen ? styles['toc-open'] : styles['toc-closed']
				}
			>
				<aside className={asideClassName.join(' ')}>
					<ul>
						{articles.map((art) => {
							const { id, name, thumbnail, components } = art;
							const title = name?.[lang];
							let readTimeText = '';
							if (readTime?.enabled) {
								let { minutes, seconds } = getReadTime(
									components,
									lang
								);
								if (minutes < 1) {
									readTimeText = ' 1 min';
								} else {
									if (seconds > 30) {
										minutes += 1;
									}

									readTimeText =
										minutes === 1
											? ` 1 min`
											: ` ${minutes} min`;
								}
							}

							if (title) {
								return (
									<li
										id={`toc-article-${id}`}
										key={id}
										onClick={() => {
											onTocItemClick(art);
											toggleToc();
										}}
										className={
											art.id === article.id
												? styles['selected']
												: null
										}
									>
										<div>
											<img src={thumbnail} />
										</div>
										<div>
											<h4>{title}</h4>
											{!readTimeText ? null : (
												<small>
													{readTimeText} read
												</small>
											)}
										</div>
									</li>
								);
							}
						})}
					</ul>
				</aside>
				<section className={styles['content']}>
					<ProgressBar />
					<Content />
				</section>
			</main>
			<Toolbar />
			<PlayerToolbar />
		</div>
	);
};

const SearchMode = () => {
	const { searchTerm } = useContext(ResponsiveMobileContext);
	const { results } = useContext(ResponsiveModeContext);
	const articles = results?.length ? results : [];
	let content = (
		<div className={styles['empty']}>
			<div>
				<Icon name={'search'} color={'#c4c4c4'} />
				<p>Please enter a search term</p>
			</div>
		</div>
	);
	if (articles.length) {
		content = (
			<ul>
				{articles.map((article, i) => (
					<SearchItem key={i} article={article} />
				))}
			</ul>
		);
	} else if (searchTerm) {
		content = (
			<div className={styles['not-found']}>
				<div>
					<Icon name={'search'} color={'#000'} />
					<p>No results, please try again</p>
				</div>
			</div>
		);
	}
	return (
		<div className={styles['search-view']}>
			<SearchBar />
			<main className={styles['content']}>{content}</main>
			<Toolbar />
		</div>
	);
};

const SearchBar = () => {
	const { searchTerm, onSearchChange } = useContext(ResponsiveMobileContext);
	const { onSearch } = useContext(ResponsiveModeContext);
	const debouncedSearchTerm = useDebounce(searchTerm, 250);
	useEffect(() => {
		onSearch(debouncedSearchTerm);
	}, [debouncedSearchTerm]);
	return (
		<nav className={styles['navbar']}>
			<div className={styles['search-bar']}>
				<span className={styles['icon']}>
					<Icon name={'search-alt'} color={'#fff'} />
				</span>
				<input
					type={'text'}
					placeholder={'Search'}
					value={searchTerm}
					onChange={(e) => {
						e.preventDefault();
						onSearchChange(e.target.value);
					}}
				/>
			</div>
		</nav>
	);
};

const SearchItem = ({ article }) => {
	const { id, name, thumbnail, components } = article;
	const { setMode } = useContext(ResponsiveMobileContext);
	const { onTocItemClick, lang } = useContext(ResponsiveModeContext);
	const { getReadTime, config } = useContext(AppContext);
	const { readTime } = config;
	let readTimeText = '';
	if (readTime?.enabled) {
		let { minutes, seconds } = getReadTime(components, lang);
		if (minutes < 1) {
			readTimeText = ' 1 min';
		} else {
			if (seconds > 30) {
				minutes += 1;
			}

			readTimeText = minutes === 1 ? ` 1 min` : ` ${minutes} min`;
		}
	}
	return (
		<li
			id={`item-article-${id}`}
			onClick={() => {
				onTocItemClick(article);
				setMode('issue');
			}}
		>
			<div>
				<img src={thumbnail} />
			</div>
			<div>
				<h4>{name[lang]}</h4>
				{!readTimeText ? null : <small>{readTimeText} read</small>}
			</div>
		</li>
	);
};

SearchItem.propTypes = {
	article: PropTypes.object,
};

function TOC({ articles, lang, onClick, article }) {
	const { theme } = useContext(AppContext);
	const background = theme?.toc?.background || '#fff';
	const listStyleType = theme?.toc?.listStyle || 'none';
	return (
		<ol
			className={styles['toc']}
			style={{
				background,
				listStyleType,
			}}
		>
			{articles
				.map((art, i) => {
					art.index = i;
					return art;
				})
				.filter((article) => article?.channel?.toc !== 'excluded')
				.map((art, i) => (
					<TOCItem
						key={i}
						lang={lang}
						index={i}
						article={art}
						selected={art?.id === article?.id}
						onClick={onClick}
					/>
				))}
		</ol>
	);
}

TOC.propTypes = {
	articles: PropTypes.array.isRequired,
	article: PropTypes.object,
	lang: PropTypes.string.isRequired,
	onClick: PropTypes.func.isRequired,
};

function TOCItem({ article, selected, lang, index, onClick }) {
	const { theme } = useContext(AppContext);

	const color = theme?.toc?.color || '#c4c4c4';
	const selectedColor = theme?.toc?.selectedColor || '#000';
	const textTransform = theme?.toc?.textTransform || undefined;
	const style = {
		overflow: 'hidden',
		color: selected ? selectedColor : color,
		textTransform,
		fontWeight: selected ? 'bold' : undefined,
		borderBottom: `1px groove ${theme?.toc?.background || '#000'}`,
		borderLeft: selected ? `4px solid ${selectedColor}` : undefined,
		borderRight: `1px groove ${theme?.toc?.background || '#000'}`,
	};
	const { name } = article;
	let componentCSS = `
		#toc-item-${index}:hover {
			color: ${selectedColor} !important;
			border-left: 4px solid ${selectedColor};
		}
	`;

	return (
		<li
			id={`toc-item-${index}`}
			className={`${styles['item']}`}
			style={style}
			onClick={() => {
				onClick(article);
			}}
		>
			{name[lang]}
			<JSXStyle id={`toc-${index}`}>{componentCSS}</JSXStyle>
		</li>
	);
}

TOCItem.propTypes = {
	article: PropTypes.object.isRequired,
	selected: PropTypes.bool.isRequired,
	lang: PropTypes.string.isRequired,
	index: PropTypes.number,
	onClick: PropTypes.func.isRequired,
};

export function ProgressBar() {
	const { index, articles } = useContext(ResponsiveModeContext);
	const percentage = ((index + 1) * 100) / articles.length;
	const color = '#000';
	// console.log(`Percentage: ${percentage}`);
	return (
		<div className={styles['progress-bar']}>
			<div
				style={{
					width: `${percentage}%`,
					background: color,
				}}
			/>
		</div>
	);
}

export function Content() {
	const { setIsPlayerOpen } = useContext(ResponsiveMobileContext);
	const { width, pages, index, onTransitionEnd } = useContext(
		ResponsiveModeContext
	);
	return (
		<div>
			<Carousel
				scrollDelta={5}
				type={'articles'}
				deviceWidth={width}
				disableSwipe={false}
				onTransitionEnd={(data) => {
					setIsPlayerOpen(false);
					onTransitionEnd(data);
				}}
				onStartFrameIndex={index}
				loop={false}
				frames={pages}
			/>
		</div>
	);
}

function Toolbar() {
	const { tts, config } = useContext(AppContext);
	const { isFavorite, onIsFavoriteClick, player } = useContext(
		ResponsiveModeContext
	);
	const { mode, toggleToc, setMode, togglePlayer } = useContext(
		ResponsiveMobileContext
	);
	const isPlayerEnabled = mode === 'issue' && player;
	let bookmarkButton = null;
	if (config?.bookmarks?.enabled) {
		const bookmarkLabel = isFavorite
			? 'Unbookmark article'
			: 'Bookmark article';
		bookmarkButton = (
			<button
				disabled={mode === 'search'}
				onClick={() => {
					onIsFavoriteClick();
					if (!isFavorite) {
						setTimeout(() => {
							alert('Article bookmarked');
						}, 200);
					}
				}}
				title={bookmarkLabel}
				aria-label={bookmarkLabel}
			>
				<Icon
					name={isFavorite ? 'bookmark-selected' : 'bookmark'}
					color={'#fff'}
				/>
			</button>
		);
	}
	return (
		<footer className={styles['toolbar']}>
			{mode === 'search' ? (
				<button
					onClick={() => {
						setMode('issue');
					}}
					title={'Issue'}
					aria-label={'Issue'}
				>
					<Icon name={'article'} color={'#fff'} />
				</button>
			) : (
				<button
					onClick={toggleToc}
					title={'Table of content'}
					aria-label={'Table of content'}
				>
					<Icon name={'toc'} color={'#fff'} />
				</button>
			)}
			<button
				title={'search'}
				aria-label={'search'}
				onClick={() => {
					setMode('search');
				}}
			>
				<Icon name={'search'} color={'#fff'} />
			</button>
			{bookmarkButton}
			{tts?.enabled ? (
				<button
					title={'Player'}
					aria-label={'Player'}
					disabled={!isPlayerEnabled}
					onClick={togglePlayer}>
					<Icon name={'player'} color={'#fff'} />
				</button>
			) : null}
		</footer>
	);
}

function PlayerToolbar() {
	const { article, player } = useContext(ResponsiveModeContext);
	const { isPlayerOpen } = useContext(ResponsiveMobileContext);
	const [playerState, setPlayerState] = useState(player?.getState());
	const [duration, setDuration] = useState(player?.getState());
	const [position, setPosition] = useState(null);
	const onPlay = (position) => {
		setPosition(Math.floor(position));
	};
	const isHidden = !isPlayerOpen || !article || !player;
	useEffect(() => {
		setPlayerState(player?.getState());
		setDuration(Math.floor(player?.getDuration()));
		if (!isPlayerOpen && playerState === 'playing' && player) {
			setPlayerState('stop');
			player.stop();
		}
		if (player) {
			player.onChange('position', onPlay);
		}
	}, [player, article, isPlayerOpen]);
	const { thumbnail } = article;
	let onPlayPauseClick = () => {
		setPlayerState('playing');
		player.play();
	};
	if (playerState === 'playing') {
		onPlayPauseClick = () => {
			setPlayerState('pause');
			player.pause();
		};
	}
	const onReplayClick = () => {
		player.rewind(5);
	};
	const onForwardClick = () => {
		player.forward(5);
	};
	const className = [styles['player-toolbar']];
	if (isHidden) {
		className.push(styles['hidden']);
	} else {
		className.push(styles['show']);
	}
	return (
		<div className={className.join(' ')}>
			<div className={styles['thumbnail']}>
				<img src={thumbnail} />
			</div>
			<div className={styles['controls']}>
				<button
					title={'Backwards 5 seconds'}
					aria-label={'Backwards 5 seconds'}
					onClick={onReplayClick}
					disabled={playerState !== 'playing'}
				>
					<Icon name={'replay-5'} />
				</button>
				<button title={'Play / Pause'}
					aria-label={'Play / Pause'}
					onClick={onPlayPauseClick}>
					<Icon
						name={
							playerState === 'playing' ? 'pause' : 'play-arrow'
						}
					/>
				</button>
				<button
					title={'Forward 5 seconds'}
					aria-label={'Forward 5 seconds'}
					onClick={onForwardClick}
					disabled={playerState !== 'playing'}
				>
					<Icon name={'forward-5'} />
				</button>
			</div>
			<div className={styles['progress']}>
				{duration
					? `${formatTime(position)} / ${formatTime(duration)}`
					: null}
			</div>
		</div>
	);
}

function formatTime(seconds) {
	if (isNaN(seconds)) {
		seconds = 0;
	}
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = Math.round(seconds % 60);
	return [h, m > 9 ? m : h ? '0' + m : m || '0', s > 9 ? s : '0' + s]
		.filter(Boolean)
		.join(':');
}
